export const SPLIT_MAX_COMBINATIONS = 12;
export const SPLIT_ROW_KEY = 'splitByFilter';

// Each combination lists one selected value per splitting filter, in filter order
export interface ISplitCombinationsResult {
    combinations: Array<Array<string>>;
    limitExceeded: boolean;
}

interface FilterRule {
    field: string;
    condition: string;
    data?: any;
}

function parseRuleValues(data: any): Array<string> {
    let values = data;
    if (typeof values == 'string') {
        try {
            values = JSON.parse(values);
        } catch {
            return [];
        }
    }
    return Array.isArray(values) ? values.map((value) => String(value)) : [];
}

// Only multi-value selections split the chart; "All", no selection or a single value add no split.
function getSplittingValues(filtersString: string | null): Array<Array<string>> {
    if (!filtersString) {
        return [];
    }
    let parsed: any;
    try {
        parsed = JSON.parse(filtersString);
    } catch {
        return [];
    }
    // Filters come as { operator, rules } from Manage Filters; a bare rules array is also accepted
    const rules: Array<FilterRule> = Array.isArray(parsed) ? parsed : (parsed?.rules ?? []);
    return rules
        .filter((rule) => rule.condition == 'is in list')
        .map((rule) => parseRuleValues(rule.data))
        .filter((values) => values.length > 1);
}

export function getSplitCombinations(filtersString: string | null): ISplitCombinationsResult {
    const splittingValues = getSplittingValues(filtersString);
    if (splittingValues.length == 0) {
        return { combinations: [], limitExceeded: false };
    }
    const total = splittingValues.reduce((count, values) => count * values.length, 1);
    if (total > SPLIT_MAX_COMBINATIONS) {
        return { combinations: [], limitExceeded: true };
    }

    let combinations: Array<Array<string>> = [[]];
    splittingValues.forEach((values) => {
        combinations = combinations.flatMap((labels) => values.map((value) => [...labels, value]));
    });
    return { combinations, limitExceeded: false };
}

export function getSplitCell(
    row: Record<string, any>,
    combination: Array<string>,
): Record<string, any> | undefined {
    return combination.reduce((node, value) => node?.[value], row[SPLIT_ROW_KEY]);
}
